// Basic
import { useEffect, useState } from 'react';
// Libs
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
// Components
import StatsChart from '@/common-utils/components/renders/chartsCards/StatsChart';
import { Separator } from '@/common-utils/components/ui/separator';
import Entity from '@/common-utils/components/renders/entity/Entity';
// Common
import ICONS from '@/common-utils/enums/icons';
import POSTERMINAL_STATS_FIELDS from '../../fields/PosterminalStatsFields';
import SIM_CARDS_STATS_FIELDS from '../../fields/SimCardsStatsFields';
// Lang
import { nsDashboard } from '@/app/i18n/namespaces';
import SIM_OFFERS_PROVIDERS from '@/app/modules/sims/enums/SimOffersProviders';

/**
 * @param root0
 * @param root0.dashboardData
 * @returns {JSX} GlobalStats
 */
function GlobalStats({ dashboardData }) {
    // Constants
    const SIM_IN_STOCK_THRESHOLD = 50;

    // Hooks
    const { i18n } = useTranslation();

    // State
    const [statsData, setStatsData] = useState([]);

    // Functions
    const getNumberValue = (key, isReduce = false) => {
        return Number(isReduce ? dashboardData.simCards?.data?.items?.reduce((acc, item) => acc + item[key], 0) || 0 :
            dashboardData.posTerminalAndSubscriptions?.data?.[key] || 0).toLocaleString(i18n.language);
    };

    const getSimCardCountWithProvider = (key, provider) => {
        return Number(dashboardData.simCards?.data?.items?.find((item) =>
            item[SIM_CARDS_STATS_FIELDS.SIM_PROVIDER.key] === provider)
            ?.[key] || 0)
            .toLocaleString(i18n.language);
    };

    // Hooks
    useEffect(() => {
        if (dashboardData) {
            setStatsData([
                {
                    label: 'global_stats.pos_terminals.title',
                    value: () => (<Entity entity={getNumberValue(POSTERMINAL_STATS_FIELDS.ACTIVE_SUBSCRIPTIONS_TOTAL.key)} className='text-xl font-semibold ' />),
                    icon: ICONS.POS_TERMINAL,
                    tooltip: 'global_stats.pos_terminals.tooltip',
                    isFetching: dashboardData?.posTerminalAndSubscriptions?.isFetching,
                    namespace: nsDashboard
                },
                // {
                //     label: 'global_stats.remote_collection_24h_count.title',
                //     value: () => (<Entity entity={getNumberValue(POSTERMINAL_STATS_FIELDS.REMOTE_COLLECTION_24H_COUNT.key)} className='text-xl font-semibold ' />),
                //     icon: ICONS.EMERGENCY_REMOTE_COLLECTION,
                //     tooltip: 'global_stats.remote_collection_24h_count.tooltip',
                //     isFetching: dashboardData?.posTerminalAndSubscriptions?.isFetching,
                //     namespace: nsDashboard
                // },
                {
                    label: 'global_stats.subscriptions.title',
                    value: () => (<Entity entity={getNumberValue(POSTERMINAL_STATS_FIELDS.ACTIVE_SUB_BLOCKED_POS_COUNT.key)} className='text-xl font-semibold ' />),
                    icon: ICONS.CALENDAR_PLUS,
                    tooltip: 'global_stats.subscriptions.tooltip',
                    isFetching: dashboardData?.simCards?.isFetching,
                    namespace: nsDashboard
                },
                {
                    label: 'global_stats.sim_cards.stock.title',
                    value: () => (
                        <>
                            {
                                getNumberValue(SIM_CARDS_STATS_FIELDS.INACTIVE_SIM_COUNT.key, true) < SIM_IN_STOCK_THRESHOLD ?
                                    < Entity
                                        entity={getNumberValue(SIM_CARDS_STATS_FIELDS.INACTIVE_SIM_COUNT.key, true)}
                                        className='text-md font-semibold '
                                    />
                                    : <div className='flex items-center gap-2 my-1'>
                                        <Entity
                                            entity='global_stats.sim_cards.stock.BOUYGUES_TELECOM'
                                            data={{
                                                number:
                                                    getSimCardCountWithProvider(SIM_CARDS_STATS_FIELDS.INACTIVE_SIM_COUNT.key, SIM_OFFERS_PROVIDERS.BOUYGUES_TELECOM.name)
                                            }}
                                            namespace={nsDashboard}
                                            className='text-sm font-semibold '
                                        />
                                        <Separator orientation='vertical' className='h-4' />
                                        <Entity
                                            entity='global_stats.sim_cards.stock.WIRELESS_LOGIC'
                                            data={{
                                                number:
                                                    getSimCardCountWithProvider(SIM_CARDS_STATS_FIELDS.INACTIVE_SIM_COUNT.key, SIM_OFFERS_PROVIDERS.WIRELESS_LOGIC.name)
                                            }}
                                            namespace={nsDashboard}
                                            className='text-sm font-semibold '
                                        />
                                    </div>
                            }
                        </>
                    ),
                    icon: ICONS.WARNING,
                    tooltip: 'global_stats.sim_cards.stock.tooltip',
                    isFetching: dashboardData?.simCards?.isFetching,
                    namespace: nsDashboard
                },
                {
                    label: 'global_stats.sim_cards.active.title',
                    value: () => (<Entity entity={getNumberValue(SIM_CARDS_STATS_FIELDS.ACTIVE_SIM_COUNT.key, true)} className='text-xl font-semibold ' />),
                    icon: ICONS.SIM_CARD,
                    tooltip: 'global_stats.sim_cards.active.tooltip',
                    isFetching: dashboardData?.simCards?.isFetching,
                    namespace: nsDashboard
                }
            ]);
        }
    }, [dashboardData]);

    return (
        <div className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4 mt-4'>
            {statsData.map((item) => (
                <StatsChart
                    key={item?.label}
                    valueContent={item?.value()}
                    isLoading={item?.isFetching}
                    label={item?.label}
                    icon={item?.icon}
                    tooltip={item?.tooltip}
                    namespace={item?.namespace}
                />
            ))}
        </div>
    );
}

GlobalStats.propTypes = {
    dashboardData: PropTypes.object.isRequired
};

export default GlobalStats;